import { useEffect } from "react";
import type Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import { getOrCreateVisitorId } from "@/app/attribution/visitor-id";
import type { AttributionTouch } from "@coffitivity/shared";
import { useApp } from "./app-provider";

/** Query parameters that mark an arrival as campaign-driven. */
const CAMPAIGN_PARAMS = {
	utm_source: "source",
	utm_medium: "medium",
	utm_campaign: "campaign",
	utm_content: "content",
	utm_term: "term",
	gclid: "gclid",
} as const;

/**
 * `gclid` is read but deliberately left in the address bar.
 *
 * Google's tag loads async, so its inline `config` call only queues work — the
 * click id is not read off the URL, and `_gcl_aw` is not set, until
 * googletagmanager.com has downloaded and run. Our bundle is local and may be
 * cached, so stripping the click id here can beat Google's tag to it and
 * silently break the conversion import that a later build depends on.
 *
 * The reasons for stripping barely apply to it anyway: a click id is unique per
 * click, so a shared URL carrying one cannot mint an entire fake campaign the
 * way a shared `utm_campaign` can.
 */
const PARAMS_TO_STRIP = Object.keys(CAMPAIGN_PARAMS).filter(
	(param) => param !== "gclid",
);

/**
 * Records an Attribution Touch when the current URL carries campaign
 * parameters, then removes the utm parameters from the address bar.
 *
 * Nothing happens — and no identifier is stored — for an ordinary visit, so
 * routine browsing leaves nothing behind.
 *
 * Stripping matters for two reasons: a hard refresh is a fresh mount that would
 * otherwise re-read the same parameters and record the arrival twice, and a URL
 * copied out of the address bar into a chat or a bookmark would keep minting
 * paid-campaign touches for people who never saw the ad. `gclid` is the
 * exception and stays — see PARAMS_TO_STRIP.
 *
 * Returns true if a touch was recorded.
 */
export function processAttribution(
	location: { search: string; pathname: string; hash: string },
	referrer: string,
	recordFn: (_touch: AttributionTouch) => Promise<unknown>,
	cookieJar?: Cookies,
): boolean {
	const params = new URLSearchParams(location.search);

	const campaign: Partial<AttributionTouch> = {};
	let hasCampaignParam = false;

	for (const [param, field] of Object.entries(CAMPAIGN_PARAMS)) {
		const value = params.get(param);
		if (value) {
			campaign[field] = value;
			hasCampaignParam = true;
		}
	}

	if (!hasCampaignParam) return false;

	const visitorId = getOrCreateVisitorId(cookieJar);

	recordFn({
		visitorId,
		...campaign,
		landingPage: location.pathname,
		referrer,
	}).catch((error) => {
		Sentry.captureException(error);
	});

	// Only our own parameters are removed — anything else the URL carried, the
	// referral code and Google's click id included, is left for its owner.
	for (const param of PARAMS_TO_STRIP) {
		params.delete(param);
	}

	const newSearch = params.toString();
	const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}${location.hash}`;
	window.history.replaceState({}, "", newUrl);

	return true;
}

export default function AttributionProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const { appManager } = useApp();

	useEffect(() => {
		processAttribution(
			window.location,
			document.referrer,
			appManager.recordAttributionTouch,
		);
	}, [appManager.recordAttributionTouch]);

	return <>{children}</>;
}
